/* Mobile: autoplay in view. Desktop: hover starts playback. Optional loops repeat while in view. */
(() => {
  if (customElements.get('sam-feature-video')) return;

  class SamFeatureVideo extends HTMLElement {
    connectedCallback() {
      this.video = this.querySelector('video');
      if (!this.video) return;
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.hoverQuery = window.matchMedia('(min-width: 769px) and (hover: hover) and (pointer: fine)');
      this.visible = false;
      this.hovering = false;
      this.focused = false;
      this.video.muted = true;
      this.video.addEventListener('ended', () => this.finish(), { signal });
      this.motionQuery.addEventListener('change', () => this.updatePlayback(), { signal });
      this.hoverQuery.addEventListener('change', () => this.updatePlayback(), { signal });
      navigator.connection?.addEventListener('change', () => this.updatePlayback(), { signal });
      this.addEventListener('pointerenter', () => {
        this.hovering = true;
        this.updatePlayback();
      }, { signal });
      this.addEventListener('pointerleave', () => {
        this.hovering = false;
        this.updatePlayback();
      }, { signal });
      this.addEventListener('focusin', () => {
        this.focused = true;
        this.updatePlayback();
      }, { signal });
      this.addEventListener('focusout', () => {
        this.focused = false;
        this.updatePlayback();
      }, { signal });
      document.addEventListener('visibilitychange', () => this.updatePlayback(), { signal });
      this.observer = new IntersectionObserver(([entry]) => {
        this.visible = entry.isIntersecting && entry.intersectionRatio >= 0.3;
        this.updatePlayback();
      }, { threshold: [0, 0.3] });
      this.observer.observe(this);
      this.updatePlayback();
    }

    finish() {
      if (this.video.loop) return;
      this.finished = true;
      this.video.pause();
      this.classList.add('is-finished');
      this.observer?.disconnect();
    }

    updatePlayback() {
      if (this.finished) return;
      const connection = navigator.connection;
      const slowConnection = connection?.saveData
        || ['slow-2g', '2g'].includes(connection?.effectiveType)
        || (connection?.downlink > 0 && connection.downlink < 1);
      const canStart = !this.hoverQuery.matches || this.hovering || this.focused;
      if (document.hidden || this.motionQuery.matches || slowConnection
        || (this.video.loop && !this.visible)
        || (!this.started && (!this.visible || !canStart))) {
        this.video.pause();
        return;
      }
      if (!this.loaded) {
        const source = this.video.querySelector('source');
        source.src = source.dataset.src;
        this.video.load();
        this.loaded = true;
      }
      this.started = true;
      this.video.play().catch(() => {
        // An interrupted play can resume on re-entry; blocked autoplay keeps the poster.
      });
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
      this.video?.pause();
    }
  }

  customElements.define('sam-feature-video', SamFeatureVideo);
})();
